import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { TextInput, Button, Notification } from '@telus-uds/components-web';
import Select from 'react-select';

import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { FormContainer, Label } from './css';

const ServiceForm = ({
  additionalServices,
  setAdditionalServices,
  servicesList,
  editService,
  setEditService,
  setIsFormTouched
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    formButtons: { saveButtonLabel, cancelButtonLabel },
    assigneeSRTModal: { addButton },
    callCentre: {
      labelServices,
      tableHeader: { labelPriceUpfront, labelQuantity },
      placeholderService
    }
  } = localizedStrings;

  const [formValues, setFormValues] = useState({
    serviceName: '',
    servicePrice: '',
    quantity: '',
    formError: false
  });

  const getServiceDataObject = () => {
    const services = [...additionalServices];
    if (editService) {
      const serviceIndex = _.findIndex(additionalServices, {
        id: editService
      });
      services[serviceIndex].name = formValues.serviceName.value;
      services[serviceIndex].price = +formValues.servicePrice;
      services[serviceIndex].quantity = +formValues.quantity;
    } else {
      services.push({
        id: uuidv4(),
        name: formValues.serviceName.value,
        price: +formValues.servicePrice,
        quantity: +formValues.quantity
      });
    }

    const res = [];
    services.forEach(service => {
      const data = res.find(i => i.name === service.name);
      if (!data) {
        res.push({ ...service });
      } else {
        data.quantity = +data.quantity + +service.quantity;
      }
    });

    return res;
  };

  const restForm = () => {
    setFormValues({
      ...formValues,
      serviceName: '',
      servicePrice: '',
      quantity: '',
      formError: false
    });
  };

  const addService = () => {
    if (!formValues.serviceName.value || !formValues.quantity)
      setFormValues({ ...formValues, formError: true });
    else {
      const servicesArr = getServiceDataObject();
      setAdditionalServices(servicesArr);
      setIsFormTouched(true);
      restForm();
      if (editService) setEditService('');
    }
  };

  const onServiceSelect = selectedOption => {
    const serviceValue = servicesList.find(
      service => selectedOption.value === service.value
    );
    setFormValues({
      ...formValues,
      serviceName: selectedOption,
      servicePrice: _.get(serviceValue, 'upfront', 0),
      quantity: ''
    });
  };

  const getServicesList = () => {
    const groupOptions = [];
    servicesList.forEach(({ label, value, group }) => {
      const groupIndex = _.findIndex(groupOptions, { label: group });
      if (groupIndex === -1) {
        const newGroup = {
          label: group,
          options: []
        };
        newGroup.options.push({ label, value });
        groupOptions.push(newGroup);
      } else {
        groupOptions[groupIndex].options.push({ label, value });
      }
    });
    return groupOptions;
  };

  const handleCancelEdit = () => {
    setEditService('');
    restForm();
  };

  useEffect(() => {
    if (editService) {
      const serviceDetails = additionalServices.find(
        service => service.id === editService
      );
      const { name, price, quantity: qty } = serviceDetails;
      setFormValues({
        ...formValues,
        serviceName: { label: name, value: name },
        servicePrice: price,
        quantity: qty
      });
    }
  }, [editService]);

  return (
    <FormContainer>
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <div style={{ width: '60%' }}>
          <Label for="serviceName">{labelServices}</Label>
          <Select
            id="serviceName"
            placeholder={placeholderService}
            options={getServicesList()}
            value={formValues.serviceName}
            onChange={selectedOption => {
              onServiceSelect(selectedOption);
            }}
            theme={theme => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                primary25: '#F2EFF4',
                primary: '#4b286d'
              }
            })}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div style={{ width: '20%', marginLeft: '1rem' }}>
          <TextInput
            id="servicePrice"
            label={labelPriceUpfront}
            value={formValues.servicePrice}
            type="number"
            onChange={(value) => {
              setFormValues({
                ...formValues,
                servicePrice: value
              });
            }}
          />
        </div>
        <div style={{ width: '20%', marginLeft: '1rem' }}>
          <TextInput
            id="quantity"
            label={labelQuantity}
            value={formValues.quantity}
            type="number"
            onChange={(value) => {
              setFormValues({
                ...formValues,
                quantity: value
              });
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ margin: '1rem 0 0 0.5rem' }}>
          <Button onPress={addService}>
            {editService ? saveButtonLabel : addButton}
          </Button>
        </div>
        {editService && (
          <div style={{ margin: '1rem 0 0 2rem' }}>
            <Button variant="secondary" onPress={handleCancelEdit}>
              {cancelButtonLabel}
            </Button>
          </div>
        )}
      </div>
      {formValues.formError && (
        <div style={{ marginTop: '1rem' }}>
          <Notification
            variant={{ validation: 'error' }}
            dismissible
            onDismiss={() =>
              setFormValues({
                ...formValues,
                formError: false
              })
            }
            copy="en"
          >
            Please select all required fields
          </Notification>
        </div>
      )}
    </FormContainer>
  );
};

ServiceForm.propTypes = {
  additionalServices: PropTypes.arrayOf(PropTypes.any).isRequired,
  setAdditionalServices: PropTypes.func.isRequired,
  servicesList: PropTypes.arrayOf(PropTypes.any).isRequired,
  editService: PropTypes.string.isRequired,
  setEditService: PropTypes.func.isRequired,
  setIsFormTouched: PropTypes.bool.isRequired
};

export default ServiceForm;
